
import { spyAction, IActionSpyInfo, IActionRunInfo, Lambda } from 'mobx';
import { b64EncodeUnicode } from '../b64encode';
import { getHash } from './utils';

export interface MobxActionInfo {
  // action 记录的id
  actionId: string;
  // action 开始执行，还是结束执行
  type: 'start' | 'end';
  // action 开始时间
  actionStartTime: number;
  // action 结束时间
  actionEndTime: number;
  // action 执行耗时
  actionCostTime: number;
  // action 执行函数体
  actionFn?: string;
  // action 函数名称
  actionName?: string;
  // action 所在对象的名称
  objectName?: string;
  // action 所在对象的构造函数体
  objectConstructor?: string;
  // action 所在对象上的key指
  objectKeys?: string;
  // action 执行堆栈信息
  actionStack?: string;
  // action 执行时的参数
  argv?: any;
}

export interface ActionListener {
  (actionInfo: MobxActionInfo): void;
}

/* action 执行超过三帧（一帧16ms）算作阻塞渲染 */
const MAX_ACTION_COST_TIME = 16 * 3;

class MobxAction {

  unlistener: Lambda | null = null;

  actionIdMap = new Map<number, string>();

  actionMap = new Map<string, number>();

  callback?: ActionListener;

  listen = (callback: ActionListener) => {
    if (this.unlistener) {
      // 清除上一次的监听事件
      this.unlisten();
    }
    this.callback = callback;
    this.unlistener = spyAction(this.actionHandle);
  }

  unlisten = () => {
    this.callback = undefined;
    this.unlistener?.();
  }

  getActionId = (actionId: number) => {
    if (!this.actionIdMap.get(actionId)) {
      this.actionIdMap.set(actionId, `${actionId}-${getHash()}`);
    }
    return this.actionIdMap.get(actionId) as string;
  }

  actionHandle = (spyInfo: IActionSpyInfo, runInfo: IActionRunInfo) => {
    const { type } = spyInfo;
    const actionId = this.getActionId(runInfo.actionId);
    if (type === 'start') {
      this.actionMap.set(actionId, new Date().getTime());
    } else {
      const endTime = new Date().getTime();
      const startTime = this.actionMap.get(actionId) || 0;
      const cost = endTime - startTime;
      if (cost >= MAX_ACTION_COST_TIME) {
        let actionStack;
        try {
          // 手动抛出报错用于获取 stack 信息
          throw new Error('[TestError]: mobx action get stack info');
        } catch (error: any) {
          if (error.stack) {
            // 如果支持 Error.stack, 则记录堆栈信息
            const lines = error.stack.split('\n');
            if (lines?.length > 6) {
              // 从 action 函数开始截取堆栈信息
              const stacks = lines.slice(6);
              actionStack = stacks.join('\n');
            }
          }
        }
        this.markAction(spyInfo, runInfo, startTime, endTime, actionStack);
      } else {
        // 如果 action 无需记录，清除记录
        this.actionMap.delete(actionId);
        this.actionIdMap.delete(runInfo.actionId);
      }
    }
  }

  markAction = (spyInfo: IActionSpyInfo, runInfo: IActionRunInfo, startTime: number, endTime: number, actionStack?: string) => {
    const { type, actionFn, actionName, object } = spyInfo;
    const actionId = this.getActionId(runInfo.actionId);
    const actionInfo: MobxActionInfo = {
      type,
      actionCostTime: endTime - startTime,
      actionStartTime: startTime,
      actionEndTime: endTime,
      // argv,
      actionId,
      actionName,
    };
    try {
      if (actionFn && typeof actionFn === 'function') {
        const fn = actionFn?.toString?.();
        actionInfo.actionFn = fn ? b64EncodeUnicode(fn) : '';
      }
      if (object && typeof object === 'object') {
        if (object?.constructor) {
          const objectConstructor = object.constructor.toString();
          actionInfo.objectConstructor = objectConstructor ? b64EncodeUnicode(objectConstructor) : '';
          actionInfo.objectName = object.constructor.name;
        }
        actionInfo.objectKeys = Object.keys(object).join();
      }
    } catch (error) {
    }
    if (actionStack) {
      actionInfo.actionStack = `Warning: [mobx.action]: ${actionInfo.actionName || ''} 运行了 ${actionInfo.actionCostTime} ms. \n${actionStack}`;
    }
    this.callback?.(actionInfo);
  }
}

export default MobxAction;
